import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAdminClinics, useIsAdmin } from '@/hooks/useAdmin';
import { AdminClinicRow } from '@/schema/admin';
import { Column, DataTable } from '../components/DataTable';
import { SectionCard } from '../components/SectionCard';
import { NewClinicDialog } from '../features/NewClinicDialog';
import { EM_DASH, formatNumber, formatRelative } from '../lib/format';

const PAGE_SIZE = 15;

export function AdminClinics() {
  useDocumentTitle('Admin - Clinics');
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchDraft);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const { data, isLoading } = useAdminClinics(page, PAGE_SIZE, search || undefined);
  const { isSuperAdmin } = useIsAdmin();

  const columns: Column<AdminClinicRow>[] = [
    {
      key: 'name',
      header: 'Clinic',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{row.name}</p>
          <p className="truncate text-xs text-white/45">{row.address ?? row.email ?? EM_DASH}</p>
        </div>
      ),
    },
    {
      key: 'orthodontists',
      header: 'Orthodontists',
      render: (row) => <span className="text-sm text-white/80">{formatNumber(row.orthodontistCount)}</span>,
    },
    {
      key: 'patients',
      header: 'Patients',
      render: (row) => <span className="text-sm text-white/80">{formatNumber(row.patientCount)}</span>,
    },
    {
      key: 'openCases',
      header: 'Open cases',
      render: (row) => (
        <span className="text-sm text-white/80">
          {formatNumber(row.openCaseCount)}
          <span className="text-white/40"> / {formatNumber(row.scanCount)}</span>
        </span>
      ),
    },
    {
      key: 'lastScan',
      header: 'Last scan',
      render: (row) => <span className="text-sm text-white/70">{formatRelative(row.lastScanAt)}</span>,
    },
  ];

  return (
    <>
      <SectionCard
        title="Clinics"
        description={data ? `${data.totalElements} registered` : undefined}
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <Input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Search by name, e-mail or address"
              className="pl-9"
            />
          </div>
          {isSuperAdmin && (
            <Button type="button" onClick={() => setIsCreating(true)}>
              <PlusIcon />
              New clinic
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          rows={data?.content ?? []}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/admin/clinics/${row.id}`)}
          page={page}
          totalPages={data?.totalPages ?? 0}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No clinics match this search."
        />
      </SectionCard>

      {isCreating ? <NewClinicDialog onClose={() => setIsCreating(false)} /> : null}
    </>
  );
}

export default AdminClinics;
